<template>
  <div>
    <div v-if="repoMinTxDate == undefined">
      <h2>No transaction calendar for this repo</h2>
      <p>
        This repository doesn't have any sources that provide date snapshots. To learn more about the source types, please see the <a
          :href="sourcesDocURL"
          target="_blank"
        >source types reference</a>.
      </p>
    </div>
    <div>
      <h2>Repository URL</h2>
    </div>
    <div v-if="repoMinTxDate != undefined">
      <p>
        Use the latest packages, or freeze to the packages that were available on a particular date. All dates presented by RSPM are in UTC to avoid any timezone discrepancies between environments.
      </p>
      <div
        class="menu showOnMobile"
      >
        <div class="menuItems">
          <a
            class="menuItem"
            :class="{ active: whichURL === URL_LATEST }"
            data-automation="setup-repo-url-latest"
            @click="setWhichURL(URL_LATEST)"
          >Latest</a>
          <a
            class="menuItem"
            :class="{ active: whichURL === URL_FROZEN }"
            data-automation="setup-repo-url-current"
            @click="setWhichURL(URL_FROZEN)"
          >Freeze</a>
        </div>
      </div>
      <GettingMore
        :show="gettingRepoMinTxDate"
        label="Getting transaction info"
      />

      <TransactionsCalendar
        :calendar-months="calendarMonths"
        :selected-date="selectedDate"
        :select-date="selectDate"
      />

      <div v-if="whichURL === URL_FROZEN">
        <div class="flexSpaceAfter">
          <div class="actionBar inline">
            <input
              id="lockUPSI"
              v-model="lockUPSI"
              type="checkbox"
            >
            <label for="lockUPSI">Lock Package Data</label>
            <ActionButton
              :button-class="dateAliasInfoClass"
              label="info"
              data-automation="setup-info-button"
              @doIt="toggleshowUPSILockInfo"
            />
          </div>
        </div>
        <div
          v-if="showUPSILockInfo"
          class="toggleableInfo"
          data-automation="setup-info-text"
        >
          <p>
            When enabled, RStudio Package Manager upgrades will never change the data returned
            from this URL.
          </p>
          <p>
            RStudio Package Manager upgrades may alter package metadata in the future. Changes
            to the set of packages available at this URL are unlikely but possible (link to Admin
            guide for migration details). Lock to prevent changes. Please see the <a
              :href="quickStartURL"
              target="_blank"
            >Quick Start guide</a> for more information.
          </p>
        </div>
      </div>
    </div>

    <div v-if="!binaryUsesSourceUrl && !binaryLinkDisabled">
      <div class="flexSpaceAfter">
        <p>Use source or binary packages:</p>
        <div class="actionBar inline">
          <ActionButton
            :button-class="packagesInfoClass"
            label="info"
            data-automation="setup-info-button"
            @doIt="toggleShowPackagesInfo"
          />
        </div>
      </div>
      <div
        v-if="showPackagesInfo"
        class="toggleableInfo"
        data-automation="setup-info-text"
      >
        <p>
          Source packages work with any OS and version of R - but must be compiled, and can therefore be slow to install.
        </p>
        <p>
          Binary packages are precompiled and install quickly - but you need to specify which OS you are using. You also need to ensure
          your R environment is set up correctly. See the <a
            data-automation="setup-binary-admin-guide"
            :href="binariesDocURL"
            target="_blank"
          >admin guide</a>
          for more information.
        </p>
      </div>

      <div class="menu showOnMobile">
        <div class="menuItems">
          <a
            id="source-url-link"
            class="menuItem"
            :class="{ active: whichPackages === PACKAGES_SOURCE }"
            data-automation="setup-repo-url-source"
            @click="setWhichPackages(PACKAGES_SOURCE)"
          >Source</a>
          <a
            class="menuItem"
            :class="{ active: whichPackages === PACKAGES_BINARY }"
            data-automation="setup-repo-url-binary"
            @click="setWhichPackages(PACKAGES_BINARY)"
          >Binary</a>
          <span
            style="font-size: 1.1em; padding-left: 5px;"
            data-automation="setup-binary-os-label"
          >For {{ selectedOS.display }}</span><a
            class="changeOSLink"
            data-automation="setup-binary-distro-change"
            href=""
            @click.prevent="handleChangeOS"
          >change</a>
        </div>
      </div>
    </div>

    <ChangeOSDialog
      v-if="showChangeOSDialog"
      @closeDialog="closeChangeOSDialogDialog"
    />
    <div>
      <div class="textWithButton">
        <h3>
          <span
            data-automation="setup-repo-url-label"
            style="font-size: .7em; padding-left: 5px;"
          >{{ whichPackagesLabel }}</span>
        </h3>
        <div>
          <button
            class="copyButton"
            data-clipboard-target="#addRepo"
            data-automation="setup-copy-button"
          >
            Copy
          </button>
        </div>
      </div>
      <GettingMore
        v-if="whichURL === URL_FROZEN && gettingRepoURL"
        label="Getting encoded URL from server"
      />
      <pre
        id="addRepo"
        data-automation="setup-repo-url"
      >{{ repoURL }}</pre>

      <div v-if="urlIsEncoded">
        <h3>
          <span style="font-size: .7em; padding-left: 5px;">
            The URL encodes the following snapshot references:
          </span>
        </h3>
        <ul>
          <li
            v-for="{source_name, timestamp, locked} in currentDecodedUrlInfo"
            :key="source_name"
          >
            {{ source_name }} [{{ timestampAndLockedStatus(timestamp, locked) }}]
          </li>
        </ul>
        <h3
          v-if="lockUPSI"
        >
          <span
            style="font-size: .7em; padding-left: 5px;"
          >
            Since you chose to <span style="font-style: italic;">
              Lock Package Data
            </span> (above), results from this URL are locked and will
            remain consistent regardless of RSPM upgrades.
          </span>
        </h3>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters, mapState, mapActions } from 'vuex'
import { serverPath } from '../api/http-common'
import { parseTimeToString } from '../utils/time'
import GettingMore from './GettingMore'
import ActionButton from './ActionButton'
import ChangeOSDialog from './dialogs/ChangeOSDialog'
import TransactionsCalendar from './TransactionsCalendar'
import { utcToZonedTime } from 'date-fns-tz'

const URL_LATEST = 'latest'
const URL_FROZEN = 'frozen'
const PACKAGES_SOURCE = 'source'
const PACKAGES_BINARY = 'binary'

export default {
  name: 'OverviewR',
  components: {
    TransactionsCalendar,
    GettingMore,
    ActionButton,
    ChangeOSDialog,
  },
  props: {
    forceBinariesDisabled: {
      type: Boolean,
      default: false,
    },
    urlSuffix: {
      type: String,
      default: '',
    },
  },
  data: function () {
    return {
      URL_LATEST: URL_LATEST,
      URL_FROZEN: URL_FROZEN,
      PACKAGES_SOURCE: PACKAGES_SOURCE,
      PACKAGES_BINARY: PACKAGES_BINARY,
      userRepoName: 'RSPM',
      whichURL: URL_LATEST,
      selectedDate: this.getDefaultDate(),
      whichPackages: PACKAGES_SOURCE,
      showUPSILockInfo: false,
      showPackagesInfo: false,
      showSystemDependenciesInfo: false,
      showChangeOSDialog: false,
      lockUPSI: false,
    }
  },
  computed: {
    ...mapState({
      currentRepoId: state => state.repos.currentRepoId,
      currentRepoSources: state => state.repos.currentRepoSources,
      selectedOS: state => state.packages.selectedOS,
      binaries_enabled: state => state.status.binaries_enabled,
      gettingRepoMinTxDate: state => state.transactions.gettingRepoMinTxDate,
      repoMinTxDate: state => state.transactions.repoMinTxDate,
      currentRepoUrlInfo: state => state.repos.currentRepoUrlInfo,
      gettingRepoURL: state => state.repos.gettingRepoURL,
      currentDecodedUrlInfo: state => state.repos.currentDecodedUrlInfo,
      gettingDecodedURL: state => state.repos.gettingDecodedURL,
    }),
    ...mapGetters([
      'currentRepo',
    ]),
    pmServerURL: function () {
      return serverPath()
    },
    binariesDocURL: function () {
      return `${serverPath()}__docs__/admin/serving-binaries/`
    },
    quickStartURL: function () {
      return `${serverPath()}__docs__/admin/getting-started/user-interface-overview/#getting-frozen-urls-for-improving-reproducibility`
    },
    sourcesDocURL: function () {
      return `${serverPath()}__docs__/admin/appendix/source-details/`
    },
    hasBinaries: function () {
      return this.selectedOS.binaries && !this.forceBinariesDisabled
    },
    repoURL: function () {
      let url = this.pmServerURL + this.currentRepo.name
      if (this.whichPackages === PACKAGES_BINARY && this.hasBinaries && !this.binaryUsesSourceUrl) {
        url += `/__linux__/${this.selectedOS.binaryURL}`
      }
      if (this.whichURL === URL_FROZEN) {
        if (this.currentRepoUrlInfo === undefined) {
          url = '<URL PENDING>'
        } else {
          if (this.currentRepoUrlInfo.short_url !== undefined && this.currentRepoUrlInfo.short_url !== '') {
            url += `/${this.currentRepoUrlInfo.short_url}`
            this.clearDecodedUrl()
          } else {
            url += `/${this.currentRepoUrlInfo.upsi}`
            // update the decoded contents accordingly
            this.getDecodedUrl(this.currentRepoUrlInfo.upsi)
          }
        }
      } else {
        url += '/latest'
      }
      url = url.trim()
      if (this.urlSuffix !== '') {
        url = url + '/' + this.urlSuffix
      }
      this.$emit('repoURL', url.trim())
      return url.trim()
    },
    selectedDateStringWithTimeAndTZ: function () {
      return parseTimeToString(this.selectedDate.toISOString(), this.showLocalTZ, { showTime: false, showTZ: false })
    },
    showLocalTZ: function () {
      // always show UTC until we can add a UI for toggling
      return false
      // https://github.com/rstudio/package-manager/pull/6380#issuecomment-893514517
      // return !isCranSource(this.currentRepoSources)
    },
    calendarMonths: function () {
      const months = []
      let start = this.repoMinTxDate !== undefined ? new Date(this.repoMinTxDate) : new Date()
      let end = new Date()

      // this is confusing (you'd expect to use zonedToUtcTime), but it seems to be correct where zonedToUtcTime is not.
      if (!this.showLocalTZ) {
        start = utcToZonedTime(start, 'UTC')
        end = utcToZonedTime(end, 'UTC')
      }

      for (let d = start;
      // eslint-disable-next-line no-unmodified-loop-condition
        d <= end;
        d.setDate(d.getDate() + 1)) {
        let thisDay = new Date(d)
        if (!this.showLocalTZ) {
          thisDay = utcToZonedTime(thisDay, 'UTC')
        }
        const alreadyAddedElement = months.find((element) => element.month === thisDay.getMonth() && element.year === thisDay.getFullYear())
        if (alreadyAddedElement) {
          alreadyAddedElement.activeDates.push({ date: thisDay.getDate() })
        } else {
          months.push(
            {
              month: thisDay.getMonth(),
              year: thisDay.getFullYear(),
              activeDates: [{ date: thisDay.getDate() }],
            },
          )
        }
      }

      return months
    },
    binaryUsesSourceUrl: function () {
      return this.selectedOS !== null && this.selectedOS.binaryURL === ''
    },
    binaryLinkDisabled: function () {
      return this.forceBinariesDisabled || (this.selectedOS !== null && this.selectedOS.binaryURL !== '' && !this.selectedOS.binaries)
    },
    whichPackagesLabel: function () {
      let packageType

      if (this.hasBinaries && !this.binaryUsesSourceUrl) {
        packageType = this.whichPackages === PACKAGES_SOURCE ? 'source' : 'binary'
      } else {
        packageType = ''
      }

      return (this.whichURL === URL_LATEST
        ? 'Use this URL to work with the latest ' + packageType + ' packages'
        : 'Use this URL to work with the ' + packageType + ' packages available from snapshots as of ' + this.selectedDateStringWithTimeAndTZ)
    },
    packagesInfoClass: function () {
      return this.showPackagesInfo ? 'toggleInfo current' : 'toggleInfo'
    },
    dateAliasInfoClass: function () {
      return this.showUPSILockInfo ? 'toggleInfo current' : 'toggleInfo'
    },
    haveInstructions: function () {
      return this.instructions && this.instructions.length > 0
    },
    urlIsEncoded: function () {
      return this.whichURL !== URL_LATEST && this.currentRepoUrlInfo !== undefined && this.currentRepoUrlInfo.short_url === ''
    },
  },
  watch: {
    currentRepoId: function (value) {
      this.whichURL = URL_LATEST
      this.whichPackages = PACKAGES_SOURCE
      this.lockUPSI = false
      this.getDates()
      this.getSystemDependencies()
    },
    selectedOS: function (newValue, oldValue) {
      this.getSystemDependencies()
    },
    lockUPSI: function () {
      this.updateRepoUrl()
    },
  },
  created: function () {
    this.getDates()
  },
  methods: {
    ...mapActions([
      'getRepoSystemDependencies',
      'getRepoMinTxDate',
      'getRepoUrl',
      'getDecodedUrl',
      'clearDecodedUrl',
    ]),
    updateRepoUrl: async function () {
      const utcTime = this.selectedDate.toISOString()
      await this.getRepoUrl({ repoName: this.currentRepo.name, snapshot: utcTime, locked: this.lockUPSI })
    },
    setWhichURL: function (which) {
      if (which === URL_FROZEN) {
        this.updateRepoUrl()
      }
      this.whichURL = which
    },
    setWhichPackages: function (which) {
      this.whichPackages = which
    },
    getDates: function () {
      if (this.currentRepoId) {
        this.getRepoMinTxDate({ repoId: this.currentRepoId })
      }
    },
    getSystemDependencies: function () {
      if (this.currentRepoId && this.selectedOS.sysReqs) {
        this.getRepoSystemDependencies({ repoId: this.currentRepoId, os: this.selectedOS })
      }
    },
    getDefaultDate: function () {
      // Convert the default new Date() to UTC
      var now = new Date()
      var nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds())
      return new Date(nowUTC)
    },
    toggleShowPackagesInfo: function () {
      this.showPackagesInfo = !this.showPackagesInfo
    },
    toggleshowUPSILockInfo: function () {
      this.showUPSILockInfo = !this.showUPSILockInfo
    },
    toggleShowSystemDependenciesInfo: function () {
      this.showSystemDependenciesInfo = !this.showSystemDependenciesInfo
    },
    handleChangeOS: function () {
      this.showChangeOSDialog = true
    },
    closeChangeOSDialogDialog: function () {
      this.showChangeOSDialog = false
      this.whichPackages = PACKAGES_BINARY
    },
    selectDate: function (transactionDate) {
      this.selectedDate = transactionDate
      this.setWhichURL(URL_FROZEN)
    },
    timestampAndLockedStatus: function (timestamp, locked) {
      if (timestamp === undefined) {
        return 'Latest available for this version'
      }
      return 'Last update: ' + parseTimeToString(timestamp, this.showLocalTZ, { showTime: true, showTZ: true }) + (locked ? ', locked' : '')
    },
  },
}

</script>

<style scoped>
h4, .emphasize {
  font-weight: bold;
}

p, h4 {
  font-size: 16px;
}

.changeOSLink {
  font-size: 12px;
  font-weight: normal;
  text-transform: none;
  margin-left: 15px;
}
</style>
